import { addScript } from '../util'
import {
  LandingPageNumber,
  ImageCapturePageNumber,
  HistoryPageNumber,
  ContactPageNumber,
  LoadingPageNumber,
  CertificatePageNumber,
  ExteriorPageNumber,
  VehiclePageNumber,
  AftermarketPageNumber,
  InteriorPageNumber,
  MechanicalPageNumber
} from '../../constants'
import { pushCDL, resetCDL } from './pixall/cdl'
import { getCategoryName, getBackButtonTagIndex, getPageLoadTagIndex, getConditionTabIndex } from './pixall/pageId'
import { CONDITION_INDEXES, CONDITION_TAGS, isConditionPage, isExteriorPage, isInteriorPage } from './pixall/pageMaps'
import { BrowserUtils } from '../../utils/siteUtils'

const pageView = (dataLayer, landingContext) => {
  resetCDL()
  if (!dataLayer) return

  if (landingContext?.offerIsFetching) {
    return
  }

  const { pageInfo, vehicle } = dataLayer
  const linkName = pageInfo.linkName

  const isConditionComponentPageLoad = linkName?.startsWith('pic1') || (pageInfo.page === ExteriorPageNumber && linkName === 'back')
  if (isConditionComponentPageLoad) return

  const isConditionComponentTabLoad = linkName?.startsWith('ico_condition')
  const params = {
    pageType: 'owner',
    eventProperties: {
      action: 'view',
      element: 'page',
      tagIndex: isConditionComponentTabLoad ? getConditionTabIndex(pageInfo) : getPageLoadTagIndex(pageInfo, landingContext)
    }
  }
  if (params.eventProperties.tagIndex === 'ico-fp-09' || params.eventProperties.tagIndex === 'ico-lp-10') {
    return
  }

  if (pageInfo.page === VehiclePageNumber && ['vin', 'lp'].includes(vehicle?.entry)) {
    params.eventProperties.valueEventCode = 'B4B2'
  }

  if (params.eventProperties.tagIndex) {
    console.log('pixall sendMetaData params:', params)
    window.coxAutoDataLayer.push({ ...pushCDL(dataLayer, landingContext, true), ...params })
  }
}

const sendEvent = (dataLayer, landingContext) => {
  const params = getParams(dataLayer, landingContext)
  if (params.eventProperties.element) {
    console.log('pixall sendClick params:', params.eventProperties)
    const cdlData = pushCDL(dataLayer, landingContext)
    const linkName = dataLayer?.pageInfo?.linkName || ''

    // Override product.name for ico-ud event tags
    if (linkName.startsWith('ico-ud-')) {
      cdlData.product = { ...cdlData.product, name: 'ico-unified-disposal' }
    }

    window.coxAutoDataLayer.push({ ...cdlData, ...params })
  }
}

const getParams = (dataLayer, landingContext) => {
  const componentsEnabled = {
    landing: landingContext?.usingLandingWebComponent,
    offerCreation: true // Offer creation component is now always enabled
  }
  const { pageInfo, icoResponses, certificateInfo } = dataLayer
  const { dealer } = certificateInfo || {}
  const linkName = pageInfo?.linkName || ''
  const params = {
    eventProperties: {
      action: 'click',
      element: '',
      category: pageInfo && `ico-${pageInfo.experience}`
    }
  }

  if (linkName.includes('back')) {
    // C3, D1, E5, J3, H3, L1

    const tagIndex = getBackButtonTagIndex(pageInfo)
    params.eventProperties = {
      ...params.eventProperties,
      element: 'ico-back-button',
      linkType: 'button',
      linkText: 'back',
      label: 'cta::back',
      tagIndex
    }

    // D1 - components
    if (tagIndex?.includes('011') && componentsEnabled.offerCreation) {
      delete params.eventProperties.label
      params.eventProperties = {
        ...params.eventProperties,
        tagIndex: 'ico-fe-012'
      }
    }

    if (isConditionPage(pageInfo.page)) {
      return params
    }
  }

  if (linkName === 'continue_to_contact' || linkName === 'continue_modal') {
    // H2

    params.eventProperties = {
      ...params.eventProperties,
      element: 'ico-next-button',
      linkType: 'button',
      linkText: 'next',
      label: 'cta::next',
      tagIndex: 'ico-fe-024',
      ico: icoResponses
    }
    return params
  }
  switch (pageInfo.page) {
    case LandingPageNumber:
      // new landing
      if (componentsEnabled.landing) {
        if (linkName.includes('cta::start-my-offer')) {
          if (linkName.includes('-error')) {
            // A1
            const error = linkName.replace('cta::start-my-offer-error::', '')
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-error-message',
              errorMessage: error,
              tagIndex: 'ico-fe-001'
            }

            if (linkName === 'cta::start-my-offer-error::vin_error') {
              //A2: cannot decode vin
              params.eventProperties = {
                ...params.eventProperties,
                errorMessage: 'ineligible::invalid',
                tagIndex: 'ico-fe-002'
              }
            } else if (['mileage', 'style'].some(text => linkName.includes(text))) {
              params.eventProperties = {
                ...params.eventProperties,
                errorMessage: error.replace('_error', 'select_error')
              }
            }
          } else if (linkName.includes('ineligible')) {
            // A2
            const reasons = linkName.replace('cta::start-my-offer-ineligible::', '')
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico ineligible',
              errorMessage: 'ineligible::' + reasons,
              tagIndex: 'ico-fe-002'
            }
          } else if (linkName === 'cta::start-my-offer') {
            // A3
            params.eventProperties = {
              ...params.eventProperties,
              element: 'ico-start-button',
              linkType: 'button',
              linkText: 'start my offer',
              tagIndex: 'ico-fe-003'
            }
          } else if (['step-by-step', 'faq', 'video'].some(suffix => linkName.includes(suffix))) {
            // A9, A10, A11
            const location = linkName.replace('cta::start-my-offer::', '')
            const eventIndex = {
              'step-by-step': '107',
              faq: '106',
              video: '104'
            }

            params.eventProperties = {
              ...params.eventProperties,
              element: 'ico start offer anchor button',
              location,
              tagIndex: `ico-fe-${eventIndex[location]}`
            }
          }
        } else if (linkName.startsWith('cta::go-ineligible::listedvin')) {
          // A2: active listing vin
          params.eventProperties = {
            ...params.eventProperties,
            action: 'impression',
            element: 'ico ineligible',
            errorMessage: 'ineligible::listedvin',
            tagIndex: 'ico-fe-002'
          }
        } else if (linkName === 'text-link::whereismyvin') {
          // A4
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-context-link',
            linkType: 'text-link',
            linkText: 'where-is-my-vin',
            tagIndex: 'ico-fe-004'
          }
        } else if (linkName.includes('cta::mileage::next')) {
          // A5
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-mileage-button',
            linkType: 'button',
            linkText: 'next',
            tagIndex: 'ico-fe-005'
          }
        } else if (linkName.includes('cta::style::next')) {
          // A6
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-style-button',
            linkType: 'button',
            linkText: 'next',
            tagIndex: 'ico-fe-006'
          }
        } else if (linkName === 'cta::go::ymm_manual') {
          // A7
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-ymm-button',
            linkType: 'button',
            linkText: 'start my offer',
            tagIndex: 'ico-fe-007'
          }
        } else if (linkName.startsWith('cta::proceed-without-instant-cash-offer')) {
          // A8
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico proceed button',
            linkType: 'button',
            linkText: 'proceed without instant cash offer',
            tagIndex: 'ico-fe-109'
          }
        } else if (linkName === 'cta::scan') {
          // B1 - Fure Component
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico start button scan',
            tagIndex: 'ico-fe-108'
          }
        } else if (linkName === 'button::vin-scan::success') {
          // B2 - Fure Component
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-vin-scan',
            linkType: 'camera',
            label: 'button::vin-scan::success',
            tagIndex: 'ico-fe-008'
          }
        } else if (linkName.includes('video')) {
          //A12, A13 (milestone), A14
          const suffix = linkName.replace('video_', '')
          const videoAction = suffix.includes('%') ? suffix.replace('_', '::') : suffix

          const eventIndex = { start: '101', complete: '103' }
          const index = suffix.includes('%') ? '102' : eventIndex[suffix]

          params.eventProperties = {
            ...params.eventProperties,
            action: suffix === 'start' ? 'click' : 'view',
            element: 'ico video',
            videoAction,
            videoTitle: 'how instant cash offer works',
            tagIndex: `ico-fe-${index}`
          }
        }
      }
      // old fury
      else if (linkName.includes('timbuybkhome_selltrade')) {
        if (linkName.includes('error')) {
          // A1
          const error = linkName.replace('continue_timbuybkhome_selltrade_', '').replace('timbuybkhome_selltrade_', '')
          params.eventProperties = {
            ...params.eventProperties,
            action: 'impression',
            element: 'ico-error-message',
            errorMessage: error,
            label: 'cta::go-error::' + error,
            tagIndex: 'ico-fe-001'
          }
        } else if (linkName.includes('ineligible')) {
          // A2
          const reasons = linkName.replace('continue_timbuybkhome_selltrade_vin_ineligible_', '').replace('_ineligible_', '|')
          params.eventProperties = {
            ...params.eventProperties,
            action: 'impression',
            element: 'ico-error-message',
            errorMessage: 'ineligible::' + reasons,
            label: 'cta::go-ineligible::' + reasons,
            tagIndex: 'ico-fe-002'
          }
        } else if (linkName.startsWith('pixall_continue_timbuybkhome_selltrade')) {
          // A3
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-start-button',
            linkType: 'button',
            linkText: 'go',
            label: 'cta::go',
            tagIndex: 'ico-fe-003'
          }
        } else if (linkName.includes('mileageselect')) {
          // A5
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-mileage-button',
            linkType: 'button',
            linkText: 'get-started',
            label: 'cta::mileage::next',
            tagIndex: 'ico-fe-005'
          }
        } else if (linkName.includes('styleselect')) {
          // A6
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-style-button',
            linkType: 'button',
            linkText: 'get-started',
            label: 'cta::style::next',
            tagIndex: 'ico-fe-006'
          }
        } else if (linkName.includes('_mm')) {
          // A7
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-ymm-button',
            linkType: 'button',
            linkText: 'continue',
            label: 'cta::go::ymm_manual',
            tagIndex: 'ico-fe-007'
          }
        } else if (linkName.includes('vinscan_success')) {
          // B1
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-vin-scan',
            linkType: 'camera',
            label: 'button::vin-scan::success',
            tagIndex: 'ico-fe-008'
          }
        } else if (linkName.includes('vinscan_error')) {
          // B1
          params.eventProperties = {
            ...params.eventProperties,
            element: 'ico-vin-scan',
            linkType: 'camera',
            label: 'button::vin-scan::error',
            tagIndex: 'ico-fe-008'
          }
        }
      } else if (linkName.startsWith('whereismyvin_lnk')) {
        // A4
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-context-link',
          linkType: 'text-link',
          linkText: 'where-is-my-vin',
          label: 'text-link::whereismyvin',
          tagIndex: 'ico-fe-004'
        }
      }
      break
    case VehiclePageNumber:
      if (linkName === 'continue') {
        const nextButton = {
          element: 'ico-next-button',
          linkType: 'button',
          linkText: 'next'
        }
        if (componentsEnabled.offerCreation) {
          if (linkName.includes('continue')) {
            // D2
            params.eventProperties = {
              ...params.eventProperties,
              ...nextButton,
              tagIndex: 'ico-fe-013',
              ico: icoResponses
            }
          }
        } else {
          // C1
          params.eventProperties = {
            ...params.eventProperties,
            ...nextButton,
            label: 'cta::continue',
            tagIndex: 'ico-fe-009'
          }
        }
      } else if (linkName.includes('continue_error')) {
        // C2
        const error = pageInfo.linkName.endsWith('_ineligible') ? 'ineligible' : 'error'
        params.eventProperties = {
          ...params.eventProperties,
          action: 'impression',
          element: 'ico-error-message',
          errorMessage: error,
          linkType: 'button',
          linkText: 'next',
          label: 'cta::continue_error::' + error,
          tagIndex: 'ico-fe-010'
        }
      } else if (linkName.includes('cta::mileage::next')) {
        // A5
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-mileage-button',
          linkType: 'button',
          linkText: 'next',
          tagIndex: 'ico-fe-005'
        }
      } else if (linkName.includes('cta::style::next')) {
        // A6
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-style-button',
          linkType: 'button',
          linkText: 'next',
          tagIndex: 'ico-fe-006'
        }
      }
      break
    case HistoryPageNumber:
      if (linkName === 'continue') {
        // L2
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-next-button',
          linkType: 'button',
          linkText: 'next',
          tagIndex: 'ico-fe-046'
        }
      }
      break
    case ImageCapturePageNumber:
      if (linkName.startsWith('button::addphoto_success::')) {
        // E1
        const category = linkName.replace('button::addphoto_success::', '')
        params.eventProperties = {
          ...params.eventProperties,
          action: 'upload',
          element: 'ico-photo-tile',
          linkText: getCategoryName(category),
          result: 'success',
          label: `button::addphoto_success::${category}`,
          tagIndex: 'ico-fe-014'
        }
      } else if (linkName.startsWith('button::addphoto_fail::')) {
        // E2
        const category = linkName.replace('button::addphoto_fail::', '')
        params.eventProperties = {
          ...params.eventProperties,
          action: 'upload',
          element: 'ico-photo-tile',
          linkText: getCategoryName(category),
          result: 'error',
          label: `button::addphoto_error::${category}`,
          tagIndex: 'ico-fe-015'
        }
      } else if (linkName === 'text-link::see-photo-guidelines') {
        // E3
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-context-link',
          linkType: 'text-link',
          linkText: 'see-photo-guidelines',
          label: 'text-link::see-photo-guidelines',
          tagIndex: 'ico-fe-016'
        }
      } else if (linkName === 'cta::addphoto_delete') {
        // E4
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-delete-photo-button',
          linkType: 'button',
          linkText: 'delete',
          label: 'cta::addphoto_delete',
          tagIndex: 'ico-fe-017'
        }
      }
      break
    case ExteriorPageNumber:
      params.eventProperties = {
        ...params.eventProperties,
        ...getVehicleConditionComponentProps(linkName)
      }

      if (linkName.startsWith('pic1_')) {
        const pics = linkName.split(',')
        const responseFields = pics.map(p => p.split('_')[0]).join('::')
        const responseValues = pics.map(p => p.split('_')[1]).join('::')
        // E6
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-next-button',
          linkType: 'button',
          linkText: 'next',
          label: 'cta::next',
          tagIndex: 'ico-fe-018',
          ico: {
            responseFields,
            responseValues
          }
        }
      }

      break

    case InteriorPageNumber:
      params.eventProperties = {
        ...params.eventProperties,
        ...getVehicleConditionComponentProps(linkName)
      }
      break

    case MechanicalPageNumber:
      params.eventProperties = {
        ...params.eventProperties,
        ...getVehicleConditionProps({
          linkName,
          icoResponses,
          tab: 'mechanical',
          navIndex: 'ico-fe-023' // H1
        })
      }

      break

    case AftermarketPageNumber:
      if (linkName.includes('back')) {
        // D1
        params.eventProperties = {
          ...params.eventProperties,
          ico: icoResponses
        }
      } else if (linkName === 'continue') {
        // D2
        params.eventProperties = {
          ...params.eventProperties,
          element: 'ico-next-button',
          linkType: 'button',
          linkText: 'next',
          label: 'cta::next',
          tagIndex: 'ico-fe-013',
          ico: icoResponses
        }
      }
      break
    case ContactPageNumber:
      if (linkName === 'cta::modal::skip') {
        params.eventProperties = {
          ...params.eventProperties,
          ...getVehicleConditionComponentProps(linkName)
        }
      } else if (linkName.includes('contact_info') && pageInfo.experience === 'kbb') {
        // J1
        params.eventProperties = {
          ...params.eventProperties,
          action: 'select',
          element: 'create-account-checkbox',
          label: 'cta::continue::create-acct',
          tagIndex: 'ico-fe-027'
        }
      } else if (linkName.startsWith('contact_error_')) {
        // J4
        const error = linkName.replace('contact_error_', '')
        params.eventProperties = {
          ...params.eventProperties,
          action: 'impression',
          element: 'ico-error-message',
          errorMessage: error,
          label: `cta::go-error::${error}`,
          tagIndex: 'ico-fe-030'
        }
      } else if (linkName.includes('continue_error')) {
        // J5
        params.eventProperties = {
          ...params.eventProperties,
          action: 'impression',
          element: 'ico-error-message',
          errorMessage: 'invalid',
          label: 'cta::go-error::error',
          tagIndex: 'ico-fe-031'
        }
      }
      break
    case LoadingPageNumber:
      if (linkName.includes('summary_submit_offer')) {
        // J2 Remove 219F
        params.eventProperties = {
          ...params.eventProperties,
          action: 'submit',
          element: 'ico-get-offer-button',
          linkType: 'button',
          linkText: 'get-my-offer',
          label: 'submit::ico::get-my-offer',
          tagIndex: 'ico-fe-028'
        }
      }
      break
    case CertificatePageNumber:
      if (linkName.includes('vehicle-history-report')) {
        // K10
        params.eventProperties = {
          ...params.eventProperties,
          valueEventCode: 'A369',
          action: 'click',
          element: 'vehicle-history-report',
          linkType: 'text-link',
          linkText: 'get-full-report',
          label: 'vehicle-history::autocheck::get-full-report',
          tagIndex: 'ico-fe-041',
          vendor: 'autocheck'
        }
      } else if (linkName.includes('RequestAppt_Top') || linkName.includes('button::request-appointment::top')) {
        // K1
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-request-appointment-button',
          linkType: 'button',
          linkText: 'request-appointment',
          location: 'top',
          label: 'button::request-appointment::top',
          tagIndex: 'ico-fe-032'
        }
      } else if (linkName.includes('button::request-appointment::dealer') || linkName.startsWith('Req_Appt_')) {
        // K2
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-request-appointment-button',
          linkType: 'button',
          linkText: 'request-appointment',
          location: 'dealer',
          position: dealer?.order,
          label: 'button::request-appointment::dealer',
          tagIndex: 'ico-fe-033'
        }
      } else if (linkName.includes('text-link::phone') || linkName.startsWith('tel_')) {
        // K3
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-dealer-phone',
          linkType: 'phone',
          linkText: dealer?.dealerPhone,
          location: 'dealer',
          position: dealer?.order,
          label: 'text-link::phone',
          tagIndex: 'ico-fe-034'
        }
      } else if (linkName.includes('text-link::sitelink') || linkName.startsWith('sitelink_dealerlink_')) {
        // K4
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-dealer-link',
          linkType: 'dealer-website',
          location: 'dealer',
          position: dealer?.order,
          label: 'text-link::sitelink',
          tagIndex: 'ico-fe-035'
        }
      } else if (linkName.includes('text-link::see_more_dealers') || linkName.includes('see_more_dealers')) {
        // K5
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-more-dealers-button',
          linkType: 'button',
          linkText: 'see-more-participating-dealers',
          label: 'text-link::see_more_dealers',
          tagIndex: 'ico-fe-036'
        }
      } else if (linkName.includes('submit::ico::request-appointment') || linkName.includes('submit_request_appointment')) {
        // K6
        params.eventProperties = {
          ...params.eventProperties,
          action: 'submit',
          element: 'ico-appointment-modal',
          linkType: 'button',
          linkText: 'request-appointment',
          label: 'submit::ico::request-appointment',
          tagIndex: 'ico-fe-037'
        }
      } else if (linkName.includes('text-link::get-directions')) {
        // K7
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-dealer-directions',
          linkType: 'text-link',
          linkText: 'get-directions',
          location: 'dealer',
          position: dealer?.order,
          label: 'text-link::get-directions',
          tagIndex: 'ico-fe-038'
        }
      } else if (linkName.includes('image::vdp::inventory-partial')) {
        // K8
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-inventory-photo',
          linkType: 'photo-tile',
          inventoryMatch: 'exact',
          location: 'dealer',
          position: dealer?.order,
          label: 'image::vdp::inventory-partial',
          tagIndex: 'ico-fe-039'
        }
      } else if (linkName.includes('image::vdp::inventory-exact')) {
        // K8
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-inventory-photo',
          linkType: 'photo-tile',
          inventoryMatch: 'exact',
          location: 'dealer',
          position: dealer?.order,
          label: 'image::vdp::inventory-exact',
          tagIndex: 'ico-fe-039'
        }
      } else if (linkName.includes('text-link::more-info::map')) {
        // K9
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-map',
          linkType: 'map-tool-tip',
          linkText: 'more-info',
          location: 'dealer',
          position: dealer?.order,
          label: 'text-link::more-info::map',
          tagIndex: 'ico-fe-040'
        }
      } else if (linkName.includes('scroll-milestone::')) {
        // K11
        const scrollPercent = linkName.replace('scroll-milestone::', '')
        params.eventProperties = {
          ...params.eventProperties,
          action: 'scroll',
          element: 'page',
          scrollDepth: `${scrollPercent}%`,
          tagIndex: 'ico-fe-042'
        }
      } else if (linkName.startsWith('accept-or-counteroffer')) {
        const action = linkName.replace('accept-or-counteroffer::', '')
        switch (action) {
          case 'cta::modal::accept-offer': // K11
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-accept-offer-button',
              elementText: 'accept-offer',
              location: 'counteroffer',
              tagIndex: 'ico-fe-200'
            }
            break

          case 'cta::modal::make-a-counteroffer': // K12
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-counteroffer-button',
              elementText: 'make-a-counteroffer',
              location: 'counteroffer',
              tagIndex: 'ico-fe-201'
            }
            break

          case 'cta::counteroffer::make': // K13
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-counteroffer-amount-button',
              elementText: 'make-a-counteroffer',
              location: 'counteroffer-modal',
              tagIndex: 'ico-fe-202'
            }
            break

          case 'cta::accept-offer::yes': // K14
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-counteroffer-yes-button',
              elementText: 'yes',
              location: 'counteroffer-modal',
              tagIndex: 'ico-fe-203'
            }
            break

          case 'cta::counteroffer::submit': // K15
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-counteroffer-submit-button',
              elementText: 'submit-counteroffer',
              location: 'counteroffer-modal',
              tagIndex: 'ico-fe-204'
            }
            break

          case 'cta::counteroffer::edit': // K16
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-counteroffer-edit-button',
              elementText: 'edit-counteroffer',
              location: 'counteroffer-modal',
              tagIndex: 'ico-fe-205'
            }
            break

          default:
            break
        }
      } else if (linkName.includes('button::send-my-offer-to-dealer')) {
        // K13
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-send-offer-button',
          elementText: 'send-my-offer-to-dealer',
          tagIndex: 'ico-fe-051',
          location: 'dealer',
          position: dealer?.order
        }
      } else if (linkName.includes('text-link::add-to-calendar')) {
        // K14
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-add-to-calendar-link',
          elementText: 'add-to-calendar',
          tagIndex: 'ico-fe-052'
        }
      } else if (linkName.includes('text-link::review-vehicle-details')) {
        // K15
        params.eventProperties = {
          ...params.eventProperties,
          action: 'click',
          element: 'ico-review-vehicle-details-link',
          elementText: 'review-vehicle-details',
          tagIndex: 'ico-fe-053'
        }
      } else if (linkName.startsWith('ico-ud-')) {
        const action = linkName.replace('ico-ud-', '')
        switch (action) {
          case '012': // U12
            if (dataLayer.contactInfo) {
              dataLayer.contactInfo.zip = landingContext?.offer?.prospect?.contactInfo?.zip || null
            }
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-kbyc-offer-section',
              tagIndex: 'ico-ud-012'
            }
            break

          case '013': // U13
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-kbyc-accept-offer-button',
              elementText: 'accept-offer',
              tagIndex: 'ico-ud-013'
            }
            break

          case '014': // U14
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-kbyc-accept-offer-modal',
              tagIndex: 'ico-ud-014'
            }
            break

          case '015': // U15
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-kbyc-accept-offer-modal-yes-button',
              elementText: 'yes',
              location: 'modal',
              tagIndex: 'ico-ud-015'
            }
            break

          case '016': // U16
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-kbyc-offer-accepted-modal',
              elementText: 'continue',
              tagIndex: 'ico-ud-016'
            }
            break

          case '017': // U17
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-kbyc-offer-accepted-section',
              location: 'below offer',
              tagIndex: 'ico-ud-017'
            }
            break

          case '026': // U24
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-kbyc-cert-get-started',
              elementText: 'get-started',
              tagIndex: 'ico-ud-026'
            }
            break

          case '027': // U25
            params.eventProperties = {
              ...params.eventProperties,
              action: 'impression',
              element: 'ico-kbyc-cert-success-email',
              tagIndex: 'ico-ud-027'
            }
            break

          default:
            break
        }
      } else if (linkName === 'ico_thank') {
        if (dataLayer.contactInfo) {
          dataLayer.contactInfo.zip = landingContext?.offer?.prospect?.contactInfo?.zip || null
        }
        const offerId = landingContext?.offer?.offerId
        const hadFiredCertPage = `ico_thank_219F_fired_${offerId}`
        const { initialPathParams, initialQueryParams } = BrowserUtils.getInitParams()
        const utmSource = initialQueryParams.utm_source ? initialQueryParams.utm_source : ''
        try {
          if (!sessionStorage.getItem(hadFiredCertPage)) {
            params.eventProperties.valueEventCode = '219F'
            sessionStorage.setItem(hadFiredCertPage, 'true')
          }
        } catch (error) {
          console.log('sessionStorage error:', error)
          params.eventProperties.valueEventCode = '219F'
        }
        if (utmSource === 'email') delete params.eventProperties.valueEventCode

        params.eventProperties = {
          ...params.eventProperties,
          action: 'view',
          element: 'page',
          tagIndex: getPageLoadTagIndex(pageInfo, landingContext)
        }
      } else if (linkName.startsWith('cta::add-wallet-pass')) {
        const action = linkName.replace('cta::add-wallet-pass::', '')
        switch (action) {
          case 'apple': // K12
            params.eventProperties = {
              ...params.eventProperties,
              action: 'click',
              element: 'ico-add-to-apple-wallet-button',
              tagIndex: 'ico-fe-050'
            }
            break

          default:
            break
        }
      }
      break

    default:
      break
  }

  return params
}

const getVehicleConditionComponentProps = linkName => {
  if (linkName === 'ico_condition_int' || linkName === 'ico_condition_mech') {
    // G1, H1
    const title = linkName.replace('ico_condition_', '')
    const linkText = CONDITION_TAGS[title]
    return {
      element: 'ico-vehicle-condition',
      location: 'navigation',
      linkType: 'header',
      linkText,
      label: `button::nav::${linkText}`,
      tagIndex: `ico-fe-${CONDITION_INDEXES[title]}`
    }
  }
  if (linkName === 'button::nav::exterior') {
    // F1
    return {
      element: 'ico-vehicle-condition',
      location: 'navigation',
      linkType: 'header',
      linkText: 'exterior',
      label: linkName,
      tagIndex: 'ico-fe-019'
    }
  } else if (linkName.startsWith('button::subnav')) {
    // F2, G2
    const isMechSubnav = !isExteriorPage(linkName) && !isInteriorPage(linkName)
    if (isMechSubnav) return

    const linkText = linkName.replace('button::subnav::', '').replaceAll('_', ' ')
    const subNavIndex = isExteriorPage(linkName) ? '020' : '022'
    return {
      element: 'ico-vehicle-condition',
      location: 'sub-navigation',
      linkType: 'tile',
      linkText,
      label: linkName,
      tagIndex: `ico-fe-${subNavIndex}`
    }
  } else if (linkName.startsWith('cta::modal')) {
    const commonProps = {
      element: 'ico-condition-modal',
      linkType: 'button',
      label: linkName
    }

    if (linkName.includes('return')) {
      // I1
      return {
        ...commonProps,
        linkText: 'return-to-questionnaire',
        tagIndex: 'ico-fe-025'
      }
    } else if (linkName.includes('skip')) {
      // I2
      return {
        ...commonProps,
        linkText: `yes-it's-perfect`,
        tagIndex: 'ico-fe-026'
      }
    }
  }
}

const getVehicleConditionProps = ({ linkName, icoResponses, tab, navIndex, subnavIndex }) => {
  if (!icoResponses) {
    return {}
  } else if (linkName.startsWith('sm')) {
    if (linkName.includes('sm_return_to_questionnaire_btn')) {
      // I1
      return {
        element: 'ico-condition-modal',
        linkType: 'button',
        linkText: 'return-to-questionnaire',
        label: 'cta::modal::return',
        tagIndex: 'ico-fe-025'
      }
    } else if (linkName.includes('sm_skip_to_next_steps_modal_btn')) {
      // I2
      return {
        element: 'ico-condition-modal',
        linkType: 'button',
        linkText: `yes-it's-perfect`,
        label: 'cta::modal::skip',
        tagIndex: 'ico-fe-026'
      }
    } else {
      const tileName = linkName.replace('sm_', '')
      return {
        element: 'ico-vehicle-condition',
        location: 'sub-navigation',
        linkType: 'tile',
        linkText: tileName,
        label: 'button::subnav::' + tileName,
        tagIndex: subnavIndex
      }
    }
  }

  return {
    element: 'ico-vehicle-condition',
    location: 'navigation',
    linkType: 'header',
    linkText: tab,
    label: 'button::nav::' + tab,
    tagIndex: navIndex
  }
}

const pixall_pageReady = (dataLayer, landingContext) => {
  window.coxAutoDataLayer = window.coxAutoDataLayer || []

  try {
    console.log('Pixall: pixall_pageReady ran')
    setTimeout(() => {
      pageView(dataLayer, landingContext)
    }, 1000)
  } catch (e) {
    console.log('pixall load error', e)
  }
}

const pixall_continue = (dataLayer, landingContext) => {
  window.coxAutoDataLayer = window.coxAutoDataLayer || []
  try {
    sendEvent(dataLayer, landingContext)
  } catch (e) {
    console.log('pixall continue error', e)
  }
}

const checkKbb = experience => {
  return experience === 'kbb'
}

export { pixall_pageReady, pixall_continue, sendEvent }
